import { useIntl } from 'react-intl';

import { useApolloClient } from '@apollo/client';

import { GetRequestHeadersQuery } from '@notino/shared/definitions/types';

import { usePriceConfig } from '@components/PriceLabel/usePriceConfig';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';

import getRequestHeadersQuery from '../../../../queries/requestHeaders.graphql';

type Return =
  | {
      isDualPrice: true;
      getRightSidePrice: (value: number) => number;
      getRightSideFormattedPrice: (value: number) => string;
      getLeftSidePrice: (value: number) => number;
      getLeftSideFormattedPrice: (value: number) => string;
      getSecondaryPrice: (value: number) => number;
      rightSideCurrency: string;
      rightSideCurrencyCode: string;
      leftSideCurrency: string;
    }
  | {
      isDualPrice: false;
      getRightSidePrice: null;
      getRightSideFormattedPrice: null;
      getLeftSidePrice: null;
      getLeftSideFormattedPrice: null;
      getSecondaryPrice: null;
      rightSideCurrency: null;
      rightSideCurrencyCode: null;
      leftSideCurrency: null;
    };

export const useIsDualPrice = (): Return => {
  const { dual_currency_price } = useFeatureFlags();
  const { cache } = useApolloClient();
  const { formatNumber } = useIntl();
  const { data: priceConfig } = usePriceConfig();
  const { code, symbol } = priceConfig?.currentCurrency ?? {
    code: '',
    symbol: '',
  };

  const requestHeadersData = cache.readQuery<GetRequestHeadersQuery>({
    query: getRequestHeadersQuery,
  });
  const { secondaryCurrency, isPrimaryCurrencyFirst } =
    requestHeadersData?.RequestHeaders ?? ({} as any);

  if (!dual_currency_price || !secondaryCurrency) {
    return {
      isDualPrice: false,
      getRightSidePrice: null,
      getRightSideFormattedPrice: null,
      getLeftSidePrice: null,
      getLeftSideFormattedPrice: null,
      getSecondaryPrice: null,
      rightSideCurrency: null,
      leftSideCurrency: null,
      rightSideCurrencyCode: null,
    };
  }

  const convert = (value: number) => value / secondaryCurrency.exchangeRates;
  const format = (value: number) =>
    formatNumber(value, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const getRightSidePrice = (value: number) =>
    isPrimaryCurrencyFirst ? convert(value) : value;
  const getLeftSidePrice = (value: number) =>
    isPrimaryCurrencyFirst ? value : convert(value);

  return {
    isDualPrice: true,
    getRightSidePrice,
    getLeftSidePrice,
    getRightSideFormattedPrice: (value) => format(getRightSidePrice(value)),
    getLeftSideFormattedPrice: (value) => format(getLeftSidePrice(value)),
    getSecondaryPrice: convert,
    rightSideCurrency: isPrimaryCurrencyFirst
      ? secondaryCurrency.symbol
      : symbol,
    leftSideCurrency: isPrimaryCurrencyFirst
      ? symbol
      : secondaryCurrency.symbol,
    rightSideCurrencyCode: isPrimaryCurrencyFirst
      ? secondaryCurrency.code
      : code,
  };
};
